import React, { useEffect, useState, useRef } from 'react';
import theme from 'theme';
import { styled, useMediaQuery } from '@mui/material';
import { Colors } from 'library';
import { client, queryStringifyObject } from 'shared';
import { useUserContext } from 'UserContext';

import StaffSendJob from '../ProviderSearch/StaffSendJob';
import { WORKED } from './constants';
import { Filters, Search, Results } from './components';

import type { PagingResult } from 'models';
import type { FilterProps, IProviderSearch } from './models';

export function ProvidersSearchAndFilter() {
    const { user } = useUserContext();

    const businessId = user?.businesses_active?.[0]?.id;

    const initialPageResults: PagingResult<IProviderSearch> = {
        count: 0,
        per_page: 0,
        num_pages: 0,
        results: [],
    };

    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [pageResult, setPageResult] = useState<PagingResult<IProviderSearch>>(initialPageResults);
    const [selectedProviders, setSelectedProviders] = useState<IProviderSearch[]>([]);
    const [formValues, setFormValues] = useState<FilterProps>({
        page: 1,
        searchQuery: '',
        workerType: WORKED,
        showPreferred: false,
        address: '-1',
        sort: 'name_asc',
    });
    const timeoutRef = useRef<number | null>(null);

    const small = useMediaQuery(theme.breakpoints.down('lg'));

    const getProviders = (params: string) =>
        client(`api/providers/?${params}`)
            .then((data: PagingResult<IProviderSearch>) => {
                setError(false);
                formValues.page > 1
                    ? setPageResult({ ...data, results: [...pageResult.results, ...data.results] })
                    : setPageResult(data);
            })
            .catch(() => setError(true))
            .finally(() => {
                setLoading(false);
            });

    function handleChange(value: string | boolean, formKey: string) {
        setLoading(true);

        if (formKey === 'page') {
            setFormValues({ ...formValues, page: formValues.page + 1 });
        } else {
            setFormValues({ ...formValues, page: 1, [formKey]: value });
        }
    }

    function handleSelect(checked: boolean, provider: IProviderSearch) {
        setSelectedProviders(
            selectedProviders.includes(provider) && !checked
                ? selectedProviders.filter((p) => p.id !== provider.id)
                : [...selectedProviders, provider],
        );
    }

    useEffect(() => {
        const searchParams = formValues?.searchQuery?.length ? { text: formValues.searchQuery } : {};

        const params = queryStringifyObject({
            ...searchParams,
            myproviders: formValues.workerType,
            page: formValues.page,
            page_size: 12,
            show_preferred_workers: formValues.showPreferred,
            address: formValues.address !== '-1' ? formValues.address : null,
            sort: formValues.sort,
            business_id: businessId,
        });

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = window.setTimeout(() => getProviders(params), 500);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    return (
        <Container>
            <InnerContainer>
                <Search handleChange={handleChange} />
                <ResultsAndFiltersContainer small={small}>
                    <Filters formValues={formValues} handleChange={handleChange} />
                    <ResultsContainer small={small}>
                        <Results
                            loading={loading}
                            error={error}
                            providers={pageResult.results}
                            selectedProviders={selectedProviders}
                            handleSelect={handleSelect}
                            loadMore={formValues.page < pageResult.num_pages}
                            handleChange={handleChange}
                        />
                    </ResultsContainer>
                </ResultsAndFiltersContainer>
            </InnerContainer>
            <StaffSendJob
                selectedProviders={selectedProviders}
                resetSelectedProviders={() => setSelectedProviders([])}
            />
        </Container>
    );
}

const Container = styled('div')({
    display: 'flex',
    justifyContent: 'center',
    paddingTop: 24,
    paddingBottom: 40,
    backgroundColor: Colors.white,
    minHeight: 'calc(100vh - 64px)',
});

const InnerContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: '90%',
    maxWidth: 1200,
});

const ResultsAndFiltersContainer = styled('div')(({ small }: { small: boolean }) => ({
    display: 'flex',
    flexDirection: small ? 'column' : 'row',
    marginTop: 20,
    gap: 20,
}));

const ResultsContainer = styled('div')(({ small }: { small: boolean }) => ({
    display: 'flex',
    justifyContent: 'center',
    flex: 1,
}));
